/** Local seed used when API is unavailable (dev) or as optimistic UI fallback shape. */
export type RecordRow = Record<string, string | number | boolean | null>;

export const LOCAL_SEED: Record<string, RecordRow[]> = {
  platform_items: [
    { id: "plt-1", title: "Demo sandbox", status: "Active", detail: "deployment_tier=demo_sandbox" },
    { id: "plt-2", title: "Seed baseline", status: "Loaded", detail: "seed-2026-07-26" },
    { id: "plt-3", title: "Audit trail", status: "On", detail: "DemoRolePreviewStarted events" },
  ],
  org_units: [
    { id: "org-1", code: "HFS", name: "Harbor Federal Services", type: "Legal entity", status: "Active" },
    { id: "org-2", code: "OPS", name: "Operations", type: "Division", status: "Active" },
    { id: "org-3", code: "FIN", name: "Finance", type: "Division", status: "Active" },
    { id: "org-4", code: "ENG", name: "Engineering", type: "Department", status: "Active" },
  ],
  projects: [
    {
      id: "prj-1",
      code: "HFS-1001",
      name: "NOAA Coastal Sensors",
      customer: "NOAA",
      status: "Active",
      funding: 2450000,
      finance_assignee_employee_id: "emp-blake",
      finance_assignee_name: "Morgan Blake",
    },
    {
      id: "prj-2",
      code: "HFS-1044",
      name: "USACE Hydrology Support",
      customer: "USACE",
      status: "Active",
      funding: 980000,
      finance_assignee_employee_id: "emp-ortiz",
      finance_assignee_name: "Jordan Ortiz",
    },
    {
      id: "prj-3",
      code: "HFS-1102",
      name: "GSA Facilities Mod",
      customer: "GSA",
      status: "At risk",
      funding: 1525000,
      finance_assignee_employee_id: "emp-blake",
      finance_assignee_name: "Morgan Blake",
    },
  ],
  budgets: [
    { id: "bud-1", project: "HFS-1001", scenario: "Baseline", eac: 2380000, etc: 410000, status: "Approved" },
    { id: "bud-2", project: "HFS-1044", scenario: "Baseline", eac: 960000, etc: 220000, status: "Approved" },
    { id: "bud-3", project: "HFS-1102", scenario: "Replan", eac: 1680000, etc: 540000, status: "Draft" },
  ],
  timesheets: [
    {
      id: "ts-chen-p4",
      employee_id: "emp-chen",
      employee: "Avery Chen",
      week: "2026-07-27",
      period_start: "2026-07-27",
      period_end: "2026-08-09",
      hours: 0,
      project: "01.014.000.0001.ENG",
      status: "Draft",
    },
    {
      id: "ts-chen-p3",
      employee_id: "emp-chen",
      employee: "Avery Chen",
      week: "2026-07-13",
      period_start: "2026-07-13",
      period_end: "2026-07-26",
      hours: 80,
      project: "01.014.000.0001.ENG",
      status: "Approved",
    },
    {
      id: "ts-chen-p2",
      employee_id: "emp-chen",
      employee: "Avery Chen",
      week: "2026-06-29",
      period_start: "2026-06-29",
      period_end: "2026-07-12",
      hours: 80,
      project: "01.014.000.0001.ENG",
      status: "Approved",
    },
    {
      id: "ts-chen-p1",
      employee_id: "emp-chen",
      employee: "Avery Chen",
      week: "2026-06-15",
      period_start: "2026-06-15",
      period_end: "2026-06-28",
      hours: 80,
      project: "01.014.000.0001.ENG",
      status: "Approved",
    },
    {
      id: "ts-ortiz-p4",
      employee_id: "emp-ortiz",
      employee: "Jordan Ortiz",
      week: "2026-07-27",
      period_start: "2026-07-27",
      period_end: "2026-08-09",
      hours: 80,
      project: "HFS-1044-OPS",
      status: "Submitted",
    },
    {
      id: "ts-blake-p3",
      employee_id: "emp-blake",
      employee: "Morgan Blake",
      week: "2026-07-13",
      period_start: "2026-07-13",
      period_end: "2026-07-26",
      hours: 80,
      project: "IND-GA-0001",
      status: "Rejected",
    },
  ],
  companies: [
    { id: "co-hfs", name: "Harbor Federal Services", status: "Active" },
    { id: "co-river", name: "River Labs Subsidiary", status: "Active" },
  ],
  company_timekeeping_policies: [
    {
      id: "pol-hfs-1",
      company_id: "co-hfs",
      company_name: "Harbor Federal Services",
      cadence: "biweekly",
      hour_basis: "80_per_biweekly",
      hour_increment: 0.25,
      effective_date: "2025-01-01",
      end_date: "2026-09-30",
    },
    {
      id: "pol-hfs-2",
      company_id: "co-hfs",
      company_name: "Harbor Federal Services",
      cadence: "weekly",
      hour_basis: "40_per_week",
      hour_increment: 0.25,
      effective_date: "2026-10-01",
      end_date: "",
    },
    {
      id: "pol-river-1",
      company_id: "co-river",
      company_name: "River Labs Subsidiary",
      cadence: "weekly",
      hour_basis: "40_per_week",
      hour_increment: 0.25,
      effective_date: "2025-06-01",
      end_date: "",
    },
  ],
  charge_codes: [
    {
      id: "cc-1",
      code: "01.014.000.0001.ENG",
      title: "NOAA Coastal Sensors — Engineering",
      labor_type: "ST",
      manager: "Jordan Ortiz",
      status: "open",
    },
    {
      id: "cc-2",
      code: "HFS-1044-OPS",
      title: "USACE Hydrology — Operations",
      labor_type: "ST",
      manager: "Jordan Ortiz",
      status: "open",
    },
    {
      id: "cc-3",
      code: "PT-ACCRUED",
      title: "Accrued PT",
      labor_type: "PT",
      manager: "Casey Tran",
      status: "open",
    },
    {
      id: "cc-4",
      code: "PT-SICK",
      title: "Sick Leave (PT)",
      labor_type: "PT",
      manager: "Casey Tran",
      status: "open",
    },
    {
      id: "cc-5",
      code: "PT-HOLIDAY",
      title: "Company Holiday",
      labor_type: "PT",
      manager: "Casey Tran",
      status: "open",
    },
    {
      id: "cc-6",
      code: "AT-TRAINING",
      title: "Mandatory training",
      labor_type: "AT",
      manager: "Casey Tran",
      status: "open",
    },
    {
      id: "cc-7",
      code: "LWOP-PERSONAL",
      title: "Leave Without Pay — Personal",
      labor_type: "LWOP",
      manager: "Casey Tran",
      status: "open",
    },
  ],
  expenses: [
    { id: "ex-1", employee: "A. Chen", purpose: "Site survey travel", amount: 842.15, status: "Pending" },
    { id: "ex-2", employee: "J. Ortiz", purpose: "Client workshop", amount: 214.5, status: "Approved" },
    { id: "ex-3", employee: "M. Blake", purpose: "Hardware sample", amount: 1299.0, status: "Draft" },
  ],
  labor_runs: [
    { id: "lr-1", period: "2026-07", status: "Posted", lines: 128, amount: 412000 },
    { id: "lr-2", period: "2026-06", status: "Posted", lines: 121, amount: 398500 },
  ],
  journals: [
    { id: "je-1", number: "JE-2401", period: "2026-07", source: "Labor", amount: 412000, status: "Posted" },
    { id: "je-2", number: "JE-2408", period: "2026-07", source: "AP", amount: 88420, status: "Posted" },
    { id: "je-3", number: "JE-2412", period: "2026-07", source: "Billing", amount: 265000, status: "Draft" },
  ],
  ap_vouchers: [
    { id: "ap-1", vendor: "Pacific Sensors LLC", invoice: "PS-8891", amount: 22400, status: "Open" },
    { id: "ap-2", vendor: "Metro Freight", invoice: "MF-220", amount: 1860, status: "Paid" },
    { id: "ap-3", vendor: "Northwind IT", invoice: "NW-4410", amount: 9600, status: "Matched" },
  ],
  purchase_orders: [
    { id: "po-1", number: "PO-5012", vendor: "Pacific Sensors LLC", amount: 45000, status: "Open" },
    { id: "po-2", number: "PO-5033", vendor: "Northwind IT", amount: 12000, status: "Received" },
    { id: "po-3", number: "PO-5041", vendor: "Harbor Tools", amount: 3200, status: "Draft" },
  ],
  invoices: [
    { id: "inv-1", number: "INV-9001", project: "HFS-1001", amount: 185000, status: "Sent" },
    { id: "inv-2", number: "INV-9008", project: "HFS-1044", amount: 72000, status: "Paid" },
    { id: "inv-3", number: "INV-9015", project: "HFS-1102", amount: 98000, status: "Draft" },
  ],
  contracts: [
    { id: "con-1", number: "GS-35F-0HFS", customer: "GSA", value: 5000000, status: "Active" },
    { id: "con-2", number: "NOAA-22-C-018", customer: "NOAA", value: 2450000, status: "Active" },
    { id: "con-3", number: "USACE-W912-044", customer: "USACE", value: 980000, status: "Closeout" },
  ],
  subcontracts: [
    { id: "sub-1", number: "SUB-220", vendor: "River Labs", flowdown: "Yes", amount: 310000, status: "Active" },
    { id: "sub-2", number: "SUB-241", vendor: "Coastal Fab", flowdown: "Yes", amount: 88000, status: "Active" },
  ],
  employees: [
    {
      id: "emp-chen",
      name: "Avery Chen",
      title: "Systems Engineer",
      org: "ENG",
      status: "Active",
      employment_status: "FT",
      company_id: "co-hfs",
      fte_percent: 1,
    },
    {
      id: "emp-ortiz",
      name: "Jordan Ortiz",
      title: "Project Manager",
      org: "OPS",
      status: "Active",
      employment_status: "FT",
      company_id: "co-hfs",
      fte_percent: 1,
    },
    {
      id: "emp-blake",
      name: "Morgan Blake",
      title: "Analyst",
      org: "FIN",
      status: "Active",
      employment_status: "PT32",
      company_id: "co-hfs",
      fte_percent: 0.8,
    },
    {
      id: "emp-4",
      name: "Riley Nguyen",
      title: "Buyer",
      org: "OPS",
      status: "Active",
      employment_status: "PT20",
      company_id: "co-hfs",
      fte_percent: 0.5,
    },
    {
      id: "emp-patel",
      name: "Sam Patel",
      title: "Contract Specialist",
      org: "OPS",
      status: "Active",
      employment_status: "PTV",
      company_id: "co-hfs",
      fte_percent: 0,
    },
  ],
  leave_balances: [
    { id: "lv-1", employee: "Avery Chen", employee_id: "emp-chen", type: "Accrued PT", balance: 96, unit: "hours" },
    { id: "lv-2", employee: "Avery Chen", employee_id: "emp-chen", type: "Bank PT", balance: 24, unit: "hours" },
  ],
  hr_documents: [
    { id: "hrdoc-1", name: "Non-Compete Agreement", type: "HR", status: "Signed", effective: "2024-03-01" },
    { id: "hrdoc-2", name: "Employee Handbook acknowledgment", type: "HR", status: "Signed", effective: "2024-03-01" },
    { id: "hrdoc-3", name: "Emergency contact form", type: "HR", status: "On file", effective: "2025-01-12" },
  ],
  documents: [
    { id: "doc-1", name: "NOAA contract PDF", module: "Contracts", status: "Filed" },
    { id: "doc-2", name: "Travel receipt Avery", module: "Expenses", status: "Attached" },
    { id: "doc-3", name: "DCAA timesheet pack Jul", module: "Timekeeping", status: "Ready" },
  ],
  employee_profile: [
    {
      id: "emp-chen",
      name: "Avery Chen",
      address: "1842 Harbor View Rd, Arlington, VA 22202",
      email: "avery.chen@harborfs.example",
    },
  ],
  address_requests: [
    {
      id: "addr-seed-1",
      new_address: "2201 Crystal Dr, Arlington, VA 22202",
      status: "HR processing",
      submitted_at: new Date(Date.now() - 36 * 3600_000).toISOString(),
      supervisor_at: new Date(Date.now() - 20 * 3600_000).toISOString(),
      supervisor_note: "Confirmed move within commuting distance.",
      hr_at: new Date(Date.now() - 8 * 3600_000).toISOString(),
    },
  ],
  charge_code_requests: [],
  compensation: [
    { id: "comp-1", annual: 128000, effective: "2026-01-01" },
    { id: "comp-2", annual: 118000, effective: "2025-01-01" },
    { id: "comp-3", annual: 108000, effective: "2024-03-01" },
  ],
  bonuses: [
    { id: "bon-1", date: "2026-03-15", type: "In-cycle", amount: 4500 },
    { id: "bon-2", date: "2025-12-10", type: "Out-of-cycle", amount: 2000 },
    { id: "bon-3", date: "2026-06-30", type: "In-cycle", amount: 3000 },
  ],
  w4: [
    {
      id: "w4-1",
      filing_status: "Single or Married filing separately",
      dependents: "0",
      extra_withholding: "$0",
      effective: "2026-01-01",
    },
  ],
  direct_deposit: [
    {
      id: "dd-1",
      bank: "Navy Federal Credit Union",
      account_type: "Checking",
      routing_masked: "••••••8912",
      account_masked: "••••4521",
      status: "Active",
    },
  ],
  pay_stubs: [
    { id: "ps-1", period: "2026-07-01 – 2026-07-15", pay_date: "2026-07-22", net: 3820.44, pdf: "paystub-2026-07-15.pdf" },
    { id: "ps-2", period: "2026-06-16 – 2026-06-30", pay_date: "2026-07-07", net: 3798.12, pdf: "paystub-2026-06-30.pdf" },
    { id: "ps-3", period: "2026-06-01 – 2026-06-15", pay_date: "2026-06-22", net: 3810.9, pdf: "paystub-2026-06-15.pdf" },
  ],
  emergency_contacts: [
    { id: "ec-1", name: "Sam Chen", relationship: "Spouse", phone: "(703) 555-0144", primary: "Yes" },
    { id: "ec-2", name: "Pat Chen", relationship: "Parent", phone: "(571) 555-0199", primary: "No" },
  ],
  dependents: [
    { id: "dep-1", name: "Jordan Chen", relationship: "Child", birth_year: 2018 },
    { id: "dep-2", name: "Riley Chen", relationship: "Child", birth_year: 2021 },
  ],
  org_contacts: [
    { id: "oc-1", role: "Supervisor", name: "Jordan Ortiz", email: "jordan.ortiz@harborfs.example" },
    { id: "oc-2", role: "HR business partner", name: "Casey Tran", email: "casey.tran@harborfs.example" },
  ],
  benefits: [
    { id: "ben-1", plan: "Medical — Blue Preferred PPO", status: "Enrolled", coverage: "Employee + Family" },
    { id: "ben-2", plan: "Dental — Delta", status: "Enrolled", coverage: "Employee + Family" },
    { id: "ben-3", plan: "Vision — VSP", status: "Waived", coverage: "—" },
  ],
  open_enrollment: [
    {
      id: "oe-1",
      status: "Closed",
      window: "2025-11-01 – 2025-11-21",
      next_window: "2026-11-01 (planned)",
    },
  ],
  training: [
    { id: "tr-1", name: "Annual ethics", due: "2026-08-15", status: "Due soon" },
    { id: "tr-2", name: "CUI handling", due: "2026-07-01", status: "Overdue" },
    { id: "tr-3", name: "Timekeeping policy acknowledgment", due: "2026-09-30", status: "Assigned" },
  ],
  week_time_entry: [
    { id: "wte-mon", day: "Mon", date: "2026-07-20", code: "01.014.000.0001.ENG", hours: 8 },
    { id: "wte-tue", day: "Tue", date: "2026-07-21", code: "01.014.000.0001.ENG", hours: 8 },
    { id: "wte-wed", day: "Wed", date: "2026-07-22", code: "01.014.000.0001.ENG", hours: 8 },
    { id: "wte-thu", day: "Thu", date: "2026-07-23", code: "IND-GA-0001", hours: 4 },
    { id: "wte-fri", day: "Fri", date: "2026-07-24", code: "01.014.000.0001.ENG", hours: 8 },
  ],
  inventory_items: [
    { id: "invt-1", sku: "SNS-440", name: "Coastal sensor kit", qty: 42, warehouse: "WH-1" },
    { id: "invt-2", sku: "CAB-12", name: "Marine cable 12m", qty: 180, warehouse: "WH-1" },
    { id: "invt-3", sku: "PWR-9", name: "Field power pack", qty: 27, warehouse: "WH-2" },
  ],
  work_orders: [
    { id: "wo-1", number: "WO-881", item: "SNS-440", qty: 10, status: "Released" },
    { id: "wo-2", number: "WO-890", item: "PWR-9", qty: 5, status: "Complete" },
  ],
  fixed_assets: [
    { id: "fa-1", tag: "FA-100", name: "CNC mill", cost: 82000, status: "In service" },
    { id: "fa-2", tag: "FA-214", name: "Field van", cost: 46000, status: "In service" },
  ],
  integrations: [
    { id: "int-1", name: "Payroll CSV export", health: "OK", last_run: "2026-07-25" },
    { id: "int-2", name: "Bank payment file", health: "OK", last_run: "2026-07-24" },
    { id: "int-3", name: "Webhook: invoice.sent", health: "Degraded", last_run: "2026-07-22" },
  ],
  bi_metrics: [
    { id: "bi-1", metric: "Backlog $", value: 3120000, trend: "+4%" },
    { id: "bi-2", metric: "Utilization", value: "78%", trend: "+1pt" },
    { id: "bi-3", metric: "DSO", value: 41, trend: "-2d" },
    { id: "bi-4", metric: "Open approvals", value: 14, trend: "flat" },
  ],
  helpdesk_tickets: [],
  tasks: [
    {
      id: "task-1",
      title: "Approve Avery Chen timesheet",
      queue: "Timekeeping",
      role: "ROLE-SUPERVISOR",
      status: "Open",
    },
    {
      id: "task-2",
      title: "Match voucher PS-8891",
      queue: "AP",
      role: "ROLE-AP-PROCUREMENT-INVENTORY",
      status: "Open",
    },
    {
      id: "task-3",
      title: "Review HFS-1102 replan",
      queue: "Planning",
      role: "ROLE-FINANCE-ACCOUNTING",
      status: "Open",
    },
    {
      id: "task-4",
      title: "Sign leave request — Ortiz",
      queue: "HR",
      role: "ROLE-HR-PAYROLL",
      status: "Open",
    },
    {
      id: "task-5",
      title: "Release INV-9015 draft",
      queue: "Billing",
      role: "ROLE-CONTRACTS-SUBCONTRACTS",
      status: "Open",
    },
  ],
};
